import type { Face } from "./brep";
import { faceNew } from "./brep";
import { WindingDir } from "./core";
import { intersectFacePlane } from "./intersect";
import { IntersectionKind } from "./intersectRay2";
import type { Plane } from "./planeTypes";
import type { Segment } from "./polyclip/dcel";
import { dcelFromPolygons } from "./polyclip/dcelFromPolygons";
import { polyLine2ToPolyClipPolygonLoop } from "./polyclip/polyClip";
import { setContains, setOpByPredicatePolygons } from "./polyclip/setOps";
import { clipRay2InsideAABB2, polygonToFaceLoops } from "./polyclip/trimFaceWithPlane";
import { polyLine2AABB2 } from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import type { Ray2 } from "./ray2Types";
import { faceSplitWithFacePlaneIntersectionSegments } from "./trim";

export function splitFaceByPlane(face: Face, cutPlane: Plane, errs: string[]): Face[] {
  const e = intersectFacePlane(face, cutPlane, errs);

  if (!e || e.kind === IntersectionKind.TANGENT) {
    return [face];
  }

  const finalLoopsBelow: PolyLine2[] = [];
  const finalLoopsAbove: PolyLine2[] = [];
  faceSplitWithFacePlaneIntersectionSegments(face, cutPlane, e.segments, finalLoopsBelow, finalLoopsAbove);

  const results: Face[] = [];
  if (finalLoopsBelow.length > 0) {
    results.push(faceNew(finalLoopsBelow, face.plane));
  }
  if (finalLoopsAbove.length > 0) {
    results.push(faceNew(finalLoopsAbove, face.plane));
  }

  if (results.length === 0) {
    errs.push("No loops found after splitting face.");
    return [face];
  }

  return results;
}

/**
 * Split a Face by a Ray2 that is in the plane of the Face.
 */
export function splitPolyLine2ByRay2(pl: PolyLine2, ray: Ray2): PolyLine2[] {
  return splitPolyLine2ByRay2s(pl, [ray]);
}

export function splitPolyLine2ByRay2s(pl: PolyLine2, rays: Ray2[]): PolyLine2[] {
  const aabb = polyLine2AABB2(pl);

  const raysTrimmed: Segment[] = rays.map((ray) => clipRay2InsideAABB2(ray, aabb)).filter((ray) => ray !== null);
  if (raysTrimmed.length === 0) {
    return [pl];
  }

  const ORIGINAL_FACE_ID = 0;
  const segmentIds = Array.from({ length: rays.length }, (_, i) => i + 1);

  const dcel = dcelFromPolygons({
    polygons: [[polyLine2ToPolyClipPolygonLoop(pl, false, true, WindingDir.CW)]], // id 0
    segments: raysTrimmed,
    segmentPolygonIds: segmentIds,
  });

  const polys = setOpByPredicatePolygons(dcel, (halfEdgeIndex) => {
    const face = dcel.halfEdgeToFace[halfEdgeIndex];

    const polygons = dcel.faceToPolygons[face];

    return setContains(polygons, [ORIGINAL_FACE_ID]) && setContains(polygons, segmentIds);
  });

  return polys.map((poly) => polygonToFaceLoops(poly)).flat();
}
